<template lang='pug'>
  div(v-if='isArticlesLoading')
    .articles-loader
      .loader-spinner
  div(v-else :class='{ "container": !isMobile }')
    .beauty-feed-index
      .row(v-if='layout === "withPageTitle"')
        .col-xs-12.col-md-10.offset-md-1.col-lg-8.offset-lg-2(:class='isMobile ? null : "justify-content-center"')
          .content-type-header(v-if='articles.length === 0')
            h1.title(v-text='$t("errorMessage.noArticleTitle")')
            p.text-snippet(v-text='$t("errorMessage.noArticleDescription")')
          .content-type-header(v-else)
            h1.title(v-text='articles[0].contentType.name')
            p.text-snippet(v-text='articles[0].contentType.description')

      .row(v-if='articles.length !== 0')
        template(v-for='(article, index) in articles')
          .col-xs-12.col-md-10.offset-md-1.col-lg-8.offset-lg-2(v-if='index % 3 === 0')
            article-card(v-bind='article' :isMain='true')
          //- TO IMPLEMENT: Most read articles is removed from the current phase but the foundation is already implemented in this component.
          //- .col-12(v-else-if='index === 0')
          //-   most-read-articles(v-if='!isMobile' :articles='articles.slice(0, 4)')
          .col-xs-12.col-md-10.offset-md-1.col-lg-6.offset-lg-0(v-else)
            article-card(v-bind='article')

        .justify-content-center.col-12
          pagination(
            :startPage='currentPage'
            :pageCount='pageCount'
            :full='true'
            @setPage='updateArticles'
            scrollTo='#top-beauty-feed'
          )
</template>

<script>
import ArticleCard from './article/card.vue'
import MostReadArticles from './article/most-read.vue'
import Pagination from '../shared/pagination.vue'
import isEmpty from 'lodash/isEmpty'
import { createNamespacedHelpers } from 'vuex'
import { getAllArticlesByFilters, getContentTypes } from '@/modules/content-platform-api'
import { fullUrl } from '@/modules/services/utilities'
import { formatArticlesForFilter as formatArticles, formatContentTypes } from './helpers'
import { dynamicLoadStore } from '@/mixins/store-util'
import beautyFeed from '@/store/modules/beauty-feed'

const { mapState } = createNamespacedHelpers('beautyFeed')

const getIndexArticles = async (store, page, per_page = 10, options = {}, route = {}) => {
  const { data: { data: articles, meta, included } } =
  await getAllArticlesByFilters(
    I18n.acceptLanguage,
    {
      page,
      perPage: per_page,
      filter: options
    }
  )

  let articleContentTypes = []
  if (!isEmpty(articles)) {
    articleContentTypes = included.filter(contentType => {
      return contentType.type === 'content_types' &&
        contentType.attributes.name === options.contentTypes
    })
  }

  let articleTags = {}
  if (!isEmpty(articles) && options.tags) {
    articleTags = included.filter(tag => {
      return tag.type === 'tags' &&
        tag.attributes.name === options.tags
    })
  }

  let articleFilters = {}
  if (!isEmpty(articles) && options.articleFilters) {
    articleFilters = included.filter(articleFilter => {
      return articleFilter.type === 'article_filters' &&
        articleFilter.attributes.name === options.articleFilters
    })
  }

  let selectedTab = ''
  if (options.setSelectedTab) {
    if (articleContentTypes.length > 0) {
      selectedTab = articleContentTypes.find(obj => obj.attributes.slug === route.params.filter).attributes.name
    } else {
      selectedTab = 'Home'
    }
    store.commit('beautyFeed/setSelectedTab', selectedTab)
  }

  let selectedFilter = ''
  if (options.setSelectedFilter) {
    if (articleFilters.length > 0) {
      selectedFilter = articleFilters.find(filter => filter.slug === route.params.filter).attributes.name
    } else {
      selectedFilter = 'All'
    }
    store.commit('beautyFeed/setSelectedFilter', selectedFilter)
  }

  store.commit('beautyFeed/setArticles', formatArticles(articles, articleContentTypes, articleTags, articleFilters))
  store.commit('beautyFeed/setArticleCount', meta.total)
  store.commit('beautyFeed/setPageCount', meta['total-pages'])
  store.commit('beautyFeed/setIsArticlesLoading', false)
}

export default {
  components: {
    'article-card': ArticleCard,
    'most-read-articles': MostReadArticles,
    'pagination': Pagination
  },
  mixins: [
    dynamicLoadStore({ beautyFeed })
  ],
  props: {
    layout: { type: String, default: null }
  },
  data () {
    return {
      mostReadArticleIds: []
    }
  },
  computed: {
    ...mapState([
      'articles', 'contentTypes', 'pageCount', 'isArticlesLoading',
      'selectedTab', 'selectedTag', 'selectedFilter',
      'currentPage'
    ]),
    isMobile: () => vueSupport.isMobile
  },
  watch: {
    '$route': 'refetchArticles'
  },
  created () {
    this.fetchArticles()
  },
  methods: {
    refetchArticles () {
      // proxy to avoid passing router object as page
      this.fetchArticles()
    },
    updateArticles (page) {
      this.$store.commit('beautyFeed/setCurrentPage', page)

      let route = { path: this.$route.path }
      if (page > 1) {
        route = Object.assign(route, {
          query: { page: page }
        })
      }
      this.$router.replace(route)

      this.$store.commit('beautyFeed/setIsArticlesLoading', true)
      if (this.selectedTab !== 'Home') {
        getIndexArticles(this.$store, page, 9, { contentTypes: this.selectedTab, setSelectedTab: true }, this.$route)
      } else {
        this.fetchArticles(page)
      }
    },
    async fetchArticles (page = 1) {
      if (this.$route.query.page) {
        page = parseInt(this.$route.query.page)
      }
      this.$store.commit('beautyFeed/setCurrentPage', page)

      if (this.selectedTab === 'Home') {
        this.$store.commit('beautyFeed/setSelectedTab', 'Home')
      }

      if (this.selectedFilter === '') {
        this.$store.commit('beautyFeed/setSelectedFilter', 'All')
      }

      let options = { filter: null, include: null, page }

      if (isEmpty(this.contentTypes)) {
        const { data: { data: contentTypes } } = await getContentTypes(I18n.acceptLanguage)
        this.$store.commit('beautyFeed/setContentTypes', formatContentTypes(contentTypes))
      }
      const contentTypeObj = this.contentTypes.find(obj => obj.slug === this.$route.params.filter)

      if (contentTypeObj) {
        options = {
          ...options,
          filter: { contentTypes: contentTypeObj.name }
        }
        this.$store.commit('beautyFeed/setSelectedTab', contentTypeObj.name)
      }

      if (this.$route.params.filter && !contentTypeObj) {
        this.$router.push('/beautyfeed')
        this.$store.commit('setNotification', {
          type: 'error',
          msg: this.$t('errorMessage.pageNotAvailable')
        })
      }

      const articleResp = await getAllArticlesByFilters(I18n.acceptLanguage, options)

      const getSeoDesc = (selectedTab) => {
        return {
          'new-arrivals': this.$t('guidance.newArrivals'),
          'sephora-spotlight': this.$t('guidance.sephoraSpotlight'),
          'tips-and-tricks': this.$t('guidance.tipsAndTricks'),
          'tutorials': this.$t('guidance.tutorials'),
          'buying-guide': this.$t('guidance.buyingGuide')
        }[selectedTab] || this.$t('guidance.home')
      }

      if (articleResp.data.data.length > 0) {
        const { data: { data: articles, meta, included } } = articleResp

        const articleContentTypes = included.filter(contentType => {
          if (contentTypeObj) {
            return contentType.type === 'content_types' && contentType.attributes.slug === contentTypeObj.slug
          } else {
            return contentType.type === 'content_types'
          }
        })

        this.$store.commit('beautyFeed/setArticles', formatArticles(articles, articleContentTypes))
        this.$store.commit('beautyFeed/setArticleCount', meta.total)
        this.$store.commit('beautyFeed/setPageCount', meta['total-pages'])
      } else {
        this.$store.commit('beautyFeed/setArticles', [])
        this.$store.commit('beautyFeed/setArticleCount', 0)
        this.$store.commit('beautyFeed/setPageCount', 1)
      }
      this.$store.commit('beautyFeed/setIsArticlesLoading', false)

      this.$store.commit('setSeoMetaTitle', this.selectedTab === 'Home' ? 'Beautyfeed' : this.selectedTab)
      this.$store.commit('setSeoMetaDescription', getSeoDesc(this.$route.params.filter))
      this.$store.commit('setSeoOgUrl', fullUrl(this.$route.path))
      this.$store.commit('setSeoUpdate', true)
      this.$store.commit('beautyFeed/setSelectedTag', '')
    }
  }
}
</script>

<style lang='scss'>
  [class^="col-"],
  .pagination .page {
    box-sizing: border-box;
  }
</style>

<style lang='scss' scoped>
  .content-type-header {
    .title {
      width: 100%;
      font-size: 24px;
      text-align: center;
      line-height: 29px;

      margin-bottom: 8px;
    }

    .text-snippet {
      text-align :center;
      word-wrap: break-word;
      margin-bottom: 40px;
      color: $grey-dark;
    }

    @media (max-width: 768px) {
      padding: 0 10px;
      .title, .text-snippet {
        text-align: left;
      }
    }
  }

  .articles-loader {
    display: flex;
    justify-content: center;
    min-height: 900px;
    padding-top: 40px;
  }

  .beauty-feed-index {
    .row {
      margin-bottom: 40px;
    }
    @media (max-width: 768px) {
      .row {
        margin-bottom: 0;
      }
    }
  }

  .justify-content-between {
    display: flex;
    justify-content: space-between;
  }

  .justify-content-center {
    display: flex;
    justify-content: center;
  }

  .pagination-container {
    margin-bottom: 50px;
  }
</style>
